import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';

type Locale = 'hr' | 'en';
type UserRow = { id: number; name: string | null; email: string | null };
type Item = {
    key: string;
    label: string;
    group: string;
    trigger: string;
    hint: string;
    configured: boolean;
    can_trigger?: boolean;
    user_ids: number[];
    emails: string[];
};
type TriggerResult = { key: string; ok: boolean; exit_code?: number; recipients?: string[]; output?: string };

const BUBBLE: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem',
    padding: '3px 6px 3px 11px', borderRadius: 999, background: '#eaf1e0',
    color: '#33461c', border: '1px solid #d3e0c1', fontWeight: 600,
};
const BUBBLE_X: React.CSSProperties = {
    border: 0, background: 'rgba(0,0,0,0.06)', color: '#5a6b3e', cursor: 'pointer',
    borderRadius: '50%', width: 18, height: 18, lineHeight: '16px', fontSize: '0.72rem', padding: 0,
};

export function AdminNotificationRecipientsSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [items, setItems] = useState<Item[]>([]);
    const [users, setUsers] = useState<UserRow[]>([]);
    const [err, setErr] = useState<string | null>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [draftUsers, setDraftUsers] = useState<Set<number>>(new Set());
    const [draftEmails, setDraftEmails] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState('');
    const [search, setSearch] = useState('');
    const [busy, setBusy] = useState(false);
    const [triggering, setTriggering] = useState<string | null>(null);
    const [triggerResult, setTriggerResult] = useState<TriggerResult | null>(null);

    const load = useCallback(() => {
        setErr(null);
        api<{ items: Item[]; users: UserRow[] }>('/admin/notification-recipients', token)
            .then((r) => { setItems(r.items ?? []); setUsers(r.users ?? []); })
            .catch((e) => setErr(String(e?.message ?? e)));
    }, [token]);
    useEffect(load, [load]);

    const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
    const groups = useMemo(() => {
        const m = new Map<string, Item[]>();
        items.forEach((it) => { if (!m.has(it.group)) m.set(it.group, []); m.get(it.group)!.push(it); });
        return Array.from(m.entries());
    }, [items]);

    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = q ? users.filter((u) => `${u.name ?? ''} ${u.email ?? ''}`.toLowerCase().includes(q)) : users;
        return base.slice(0, 60);
    }, [users, search]);

    const startEdit = (it: Item) => {
        setEditing(it.key);
        setDraftUsers(new Set(it.user_ids));
        setDraftEmails([...(it.emails ?? [])]);
        setEmailInput(''); setSearch('');
    };
    const toggleUser = (id: number) => setDraftUsers((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const addEmail = () => {
        const e = emailInput.trim().replace(/[,;]$/, '');
        if (e && /.+@.+\..+/.test(e) && !draftEmails.includes(e)) { setDraftEmails((p) => [...p, e]); setEmailInput(''); }
    };

    const save = async (key: string) => {
        setBusy(true); setErr(null);
        try {
            await mutate('/admin/notification-recipients', token, 'PUT', {
                notification_key: key, user_ids: Array.from(draftUsers), emails: draftEmails,
            });
            setEditing(null); load();
        } catch (e) { setErr(String((e as Error)?.message ?? e)); }
        finally { setBusy(false); }
    };
    const resetToDefault = async (key: string) => {
        if (!confirm(t('Vratiti na zadane primatelje (obriši override)?', 'Reset to default recipients?'))) return;
        setBusy(true);
        try { await mutate('/admin/notification-recipients?notification_key=' + encodeURIComponent(key), token, 'DELETE', {}); setEditing(null); load(); }
        catch (e) { setErr(String((e as Error)?.message ?? e)); }
        finally { setBusy(false); }
    };
    const triggerNow = async (it: Item) => {
        if (!confirm(t(`Poslati „${it.label}" ODMAH trenutnim primateljima? Ovo šalje pravi mail.`, `Send "${it.label}" NOW to current recipients? This sends a real email.`))) return;
        setTriggering(it.key); setTriggerResult(null); setErr(null);
        try {
            const r = await mutate<TriggerResult>('/admin/notification-recipients/trigger', token, 'POST', { notification_key: it.key });
            setTriggerResult({ ...r, key: it.key });
        } catch (e) { setErr(String((e as Error)?.message ?? e)); }
        finally { setTriggering(null); }
    };

    const collapsedRecipients = (it: Item) => {
        const chips = [...it.user_ids.map((id) => userById.get(id)?.name || userById.get(id)?.email || ('#' + id)), ...it.emails];
        if (chips.length === 0) {
            if (it.configured) return <span style={{ fontSize: '0.8rem', color: '#b14040', fontWeight: 600 }}>{t('nitko (ugašeno)', 'nobody (off)')}</span>;
            return <span className="muted" style={{ fontSize: '0.8rem' }}>{t('zadano', 'default')}: {it.hint}</span>;
        }
        return <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
            {chips.map((c, i) => <span key={i} style={{ ...BUBBLE, padding: '2px 9px' }}>{c}</span>)}
            {!it.configured ? <em className="muted" style={{ fontSize: '0.72rem' }}>({t('zadano', 'default')})</em> : null}
        </span>;
    };

    return (
        <div style={{ display: 'grid', gap: 14 }}>
            <div>
                <h2 style={{ margin: 0, color: 'var(--ad-ink)' }}>📬 {t('Obavijesti — primatelji', 'Notifications — recipients')}</h2>
                <p className="muted" style={{ fontSize: '0.84rem', marginTop: 4 }}>
                    {t('Tko prima koji automatski mail. Prazno = obavijest ugašena. „Vrati na zadano" briše ručnu postavku.',
                        'Who receives which automatic email. Empty = disabled. "Reset" clears the manual setting.')}
                </p>
            </div>
            {err ? <div style={{ color: '#b14040', fontSize: '0.85rem' }}>{err}</div> : null}

            {groups.map(([group, gitems]) => (
                <div key={group} style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                    <div style={{ background: '#f4f1e8', padding: '9px 14px', fontWeight: 700, color: 'var(--ad-ink)', fontSize: '0.9rem' }}>{group}</div>
                    {gitems.map((it) => (
                        <div key={it.key} style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px' }}>
                                <div style={{ minWidth: 190 }}>
                                    <div style={{ fontWeight: 600, color: 'var(--ad-ink)' }}>{it.label}</div>
                                    <div className="muted" style={{ fontSize: '0.72rem' }}>{it.trigger}</div>
                                </div>
                                <div style={{ flex: 1 }}>{collapsedRecipients(it)}</div>
                                {it.can_trigger ? (
                                    <button type="button" disabled={triggering === it.key} title={t('Pokreni odmah i pošalji mail trenutnim primateljima', 'Run now and email current recipients')}
                                        onClick={() => triggerNow(it)}
                                        style={{ fontSize: '0.76rem', whiteSpace: 'nowrap', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 8, padding: '6px 12px', cursor: triggering === it.key ? 'wait' : 'pointer', fontWeight: 700, opacity: triggering === it.key ? 0.7 : 1 }}>
                                        {triggering === it.key ? '⏳ ' + t('Šaljem…', 'Sending…') : '▶ ' + t('Okini sad', 'Trigger now')}
                                    </button>
                                ) : null}
                                <button type="button" className="secondary compact" style={{ fontSize: '0.76rem', whiteSpace: 'nowrap' }}
                                    onClick={() => (editing === it.key ? setEditing(null) : startEdit(it))}>
                                    {editing === it.key ? t('Zatvori', 'Close') : '✏️ ' + t('Uredi', 'Edit')}
                                </button>
                            </div>

                            {triggerResult && triggerResult.key === it.key ? (
                                <div style={{ margin: '0 14px 12px', padding: '10px 12px', borderRadius: 8, fontSize: '0.8rem', background: triggerResult.ok ? '#eef7f0' : '#fbecec', border: '1px solid ' + (triggerResult.ok ? '#bfe3cd' : '#e6bcbc') }}>
                                    <div style={{ fontWeight: 700, color: triggerResult.ok ? '#1f7a36' : '#b14040', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{triggerResult.ok ? '✅ ' + t('Pokrenuto', 'Executed') : '⚠ ' + t('Greška (exit ', 'Error (exit ') + (triggerResult.exit_code ?? '?') + ')'}</span>
                                        <button type="button" onClick={() => setTriggerResult(null)} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#888', fontWeight: 800 }}>✕</button>
                                    </div>
                                    {triggerResult.recipients && triggerResult.recipients.length > 0
                                        ? <div style={{ marginTop: 4, color: '#555' }}>{t('Primatelji:', 'Recipients:')} {triggerResult.recipients.join(', ')}</div>
                                        : <div style={{ marginTop: 4, color: '#b14040' }}>{t('Bez primatelja — mail nije poslan nikome.', 'No recipients — email sent to nobody.')}</div>}
                                    {triggerResult.output ? <pre style={{ marginTop: 6, whiteSpace: 'pre-wrap', color: '#666', fontSize: '0.72rem', maxHeight: 140, overflowY: 'auto' }}>{triggerResult.output}</pre> : null}
                                </div>
                            ) : null}

                            {editing === it.key ? (
                                <div style={{ padding: '0 14px 16px', background: '#fbfaf5', display: 'grid', gap: 12, maxWidth: 640 }}>
                                    {/* odabrani — soap bubbles */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 30, alignItems: 'center', paddingTop: 6 }}>
                                        {draftUsers.size === 0 && draftEmails.length === 0 ? (
                                            <span className="muted" style={{ fontSize: '0.78rem' }}>{t('Nema primatelja — obavijest je ugašena.', 'No recipients — notification off.')}</span>
                                        ) : null}
                                        {Array.from(draftUsers).map((id) => {
                                            const u = userById.get(id);
                                            return <span key={'u' + id} style={BUBBLE}>{u?.name || u?.email || ('#' + id)}
                                                <button type="button" style={BUBBLE_X} title={t('Makni', 'Remove')} onClick={() => toggleUser(id)}>✕</button></span>;
                                        })}
                                        {draftEmails.map((e) => (
                                            <span key={'e' + e} style={{ ...BUBBLE, background: '#eef0f7', color: '#33406b', borderColor: '#d3d9ec' }}>{e}
                                                <button type="button" style={BUBBLE_X} title={t('Makni', 'Remove')} onClick={() => setDraftEmails((p) => p.filter((x) => x !== e))}>✕</button></span>
                                        ))}
                                    </div>

                                    {/* quick search korisnika */}
                                    <div>
                                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus
                                            placeholder={'🔍 ' + t('Pretraži korisnike…', 'Search users…')}
                                            style={{ width: '100%', padding: '7px 10px', fontSize: '0.84rem', borderRadius: 8, border: '1px solid #d8d2c0' }} />
                                        <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6, border: '1px solid #ece7d8', borderRadius: 8, background: '#fff' }}>
                                            {filteredUsers.map((u) => {
                                                const on = draftUsers.has(u.id);
                                                return (
                                                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 11px', cursor: 'pointer', borderBottom: '1px solid #f3efe4', background: on ? '#f2f7ea' : 'transparent' }}>
                                                        <input type="checkbox" checked={on} onChange={() => toggleUser(u.id)} />
                                                        <span style={{ fontSize: '0.82rem', color: 'var(--ad-ink)' }}>{u.name || ('#' + u.id)}</span>
                                                        {u.email ? <span className="muted" style={{ fontSize: '0.74rem' }}>{u.email}</span> : null}
                                                    </label>
                                                );
                                            })}
                                            {filteredUsers.length === 0 ? <div className="muted" style={{ padding: '8px 11px', fontSize: '0.78rem' }}>{t('Nema pogodaka.', 'No matches.')}</div> : null}
                                        </div>
                                    </div>

                                    {/* slobodan email */}
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(); } }}
                                            placeholder={t('vanjski email + Enter', 'external email + Enter')}
                                            style={{ flex: 1, padding: '6px 10px', fontSize: '0.82rem', borderRadius: 8, border: '1px solid #d8d2c0' }} />
                                        <button type="button" className="secondary compact" style={{ fontSize: '0.78rem' }} onClick={addEmail}>＋ {t('Dodaj', 'Add')}</button>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <button type="button" disabled={busy} onClick={() => save(it.key)}
                                            style={{ background: 'var(--ad-acc)', color: '#fff', border: 0, padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.83rem' }}>
                                            💾 {t('Spremi', 'Save')}
                                        </button>
                                        {it.configured ? (
                                            <button type="button" disabled={busy} onClick={() => resetToDefault(it.key)} className="secondary compact" style={{ fontSize: '0.78rem' }}>
                                                ↩ {t('Vrati na zadano', 'Reset to default')}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
